import paymentEncryptionUtil from '../utils/paymentEncryption';

/**
 * API interceptor for automatic payment data encryption/decryption
 * 
 * Automatically encrypts sensitive payment data in outgoing requests
 * and decrypts sensitive data in incoming responses for payment endpoints.
 */

// Match against request URLs after axios prepends baseURL (/api/v1/...).
const PAYMENT_ENDPOINT_PATTERNS = [
    '/api/v1/payment',
    '/api/v1/purchase',
    '/api/v1/stored-payment-methods',
    '/api/v1/payment-methods',
    '/api/v1/billing',
    '/api/v1/transactions'
];

/**
 * Check if a URL is a payment-related endpoint
 * @param {string} url - The request URL
 * @returns {boolean} True if this is a payment endpoint
 */
const isPaymentEndpoint = (url) => {
    if (!url) return false;
    return PAYMENT_ENDPOINT_PATTERNS.some(pattern => url.includes(pattern));
};

/**
 * Request interceptor for encrypting outgoing payment data
 * @param {Object} config - Axios request config
 * @returns {Object} Modified request config with encrypted data
 */
export const encryptRequestInterceptor = async (config) => {
    // Only encrypt payment-related requests
    if (!isPaymentEndpoint(config.url) || !config.data) {
        return config;
    }

    // Skip encryption for GET requests (no request body)
    if (config.method?.toLowerCase() === 'get') {
        return config;
    }

    try {
        console.debug('Encrypting payment request data for:', config.url);
        
        // Encrypt sensitive fields in request data
        const encryptedData = await paymentEncryptionUtil.encryptPaymentData(config.data);
        
        // Update request config with encrypted data
        config.data = encryptedData;
        
        // Add header to indicate encrypted payload
        config.headers = config.headers || {};
        config.headers['X-Encrypted-Payload'] = 'true';
        
        return config;
    } catch (error) {
        console.error('Request encryption failed:', error);
        // Continue with original data if encryption fails
        return config;
    }
};

/**
 * Response interceptor for decrypting incoming payment data
 * @param {Object} response - Axios response object
 * @returns {Object} Modified response with decrypted data
 */
export const decryptResponseInterceptor = async (response) => {
    // Only decrypt payment-related responses
    if (!isPaymentEndpoint(response.config?.url) || !response.data) {
        return response;
    }

    try {
        // Check if response indicates encrypted payload
        const isEncrypted = response.headers['x-encrypted-payload'] === 'true' ||
                           response.data.encrypted === true;
        
        if (isEncrypted) {
            console.debug('Decrypting payment response data for:', response.config.url);
            
            // Handle encrypted wrapper response
            if (response.data.encryptedData) {
                // This is a wrapped encrypted response
                const decryptedPayload = JSON.parse(response.data.encryptedData);
                const decryptedData = await paymentEncryptionUtil.decryptPaymentData(decryptedPayload);
                response.data = decryptedData;
            } else {
                // Direct field encryption
                const decryptedData = await paymentEncryptionUtil.decryptPaymentData(response.data);
                response.data = decryptedData;
            }
        }
        
        return response;
    } catch (error) {
        console.error('Response decryption failed:', error);
        // Continue with original data if decryption fails
        return response;
    }
};

/**
 * Error interceptor to handle encryption-related errors
 * @param {Object} error - Axios error object
 * @returns {Promise} Rejected promise with error
 */
export const encryptionErrorInterceptor = async (error) => {
    // Check if error is encryption-related
    if (error.config && isPaymentEndpoint(error.config.url)) {
        console.debug('Payment endpoint error, checking for encryption issues');
        
        // If it's a 400 error, it might be due to encryption mismatch
        if (error.response?.status === 400 && error.response?.data?.message?.includes('encryption')) {
            console.warn('Possible encryption/decryption mismatch in payment endpoint');
        }
    }
    
    return Promise.reject(error);
};

/**
 * Setup encryption interceptors for an Axios instance
 * @param {Object} axiosInstance - The Axios instance to configure
 */
export const setupEncryptionInterceptors = (axiosInstance) => {
    // Request interceptor for encryption
    axiosInstance.interceptors.request.use(
        encryptRequestInterceptor,
        (error) => Promise.reject(error)
    );
    
    // Response interceptor for decryption
    axiosInstance.interceptors.response.use(
        decryptResponseInterceptor,
        encryptionErrorInterceptor
    );
};

const paymentEncryptionInterceptor = {
    encryptRequestInterceptor,
    decryptResponseInterceptor,
    encryptionErrorInterceptor,
    setupEncryptionInterceptors,
    isPaymentEndpoint
};

export default paymentEncryptionInterceptor;